import type {
  InvoiceScheduleListItem,
  InvoiceScheduleListResponse,
  InvoiceScheduleDetail,
  InvoiceScheduleCreateRequest,
  InvoiceScheduleAcceptedResponse,
  InvoiceScheduleStatusResponse,
  InvoiceScheduleUpdateRequest,
  IncrementScheduleLineRequest,
  IncrementScheduleLineResult,
  PendingScheduleLineOp,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

// Schedule create is async: the API enqueues an orchestrator message and
// returns 202 + messageId; we poll the status endpoint until terminal.
const SCHEDULE_CREATE_POLL_INTERVAL_MS = 1_500;
const SCHEDULE_CREATE_MAX_POLL_MS = 180_000; // 3 minutes

export async function getInvoiceSchedules(signal?: AbortSignal): Promise<InvoiceScheduleListItem[]> {
  const response = await fetch(`${API_BASE_URL}/invoice-schedules`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch invoice schedules: ${response.status} ${response.statusText}`);
  }

  const data: InvoiceScheduleListResponse = await response.json();
  return data.items;
}

/**
 * List invoice schedules for a single customer.
 * @param dataverseCustomerId the Dataverse customer GUID (cr001_customersid) —
 *   the same id surfaced as `customerId` on each list item.
 */
export async function getCustomerInvoiceSchedules(
  dataverseCustomerId: string,
  signal?: AbortSignal,
): Promise<InvoiceScheduleListItem[]> {
  const response = await fetch(
    `${API_BASE_URL}/invoice-schedules?customerId=${encodeURIComponent(dataverseCustomerId)}`,
    {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch customer invoice schedules: ${response.status} ${response.statusText}`);
  }

  const data: InvoiceScheduleListResponse = await response.json();
  return data.items;
}

export async function getInvoiceSchedule(id: string, signal?: AbortSignal): Promise<InvoiceScheduleDetail> {
  const response = await fetch(`${API_BASE_URL}/invoice-schedules/${encodeURIComponent(id)}`, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch invoice schedule: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Enqueue a schedule create. The API creates the schedule in RepairShopr and
 * mirrors it to Dataverse via the orchestrator, so this returns 202 Accepted
 * with a messageId rather than the finished schedule. Use
 * {@link createInvoiceScheduleAndWait} to also poll to completion.
 */
export async function createInvoiceSchedule(
  payload: InvoiceScheduleCreateRequest,
  signal?: AbortSignal
): Promise<InvoiceScheduleAcceptedResponse> {
  const response = await fetch(`${API_BASE_URL}/invoice-schedules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to create invoice schedule: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }

  return response.json();
}

/** Poll the create status for a previously-enqueued schedule message. */
export async function getInvoiceScheduleCreationStatus(
  messageId: string,
  signal?: AbortSignal,
): Promise<InvoiceScheduleStatusResponse> {
  const response = await fetch(
    `${API_BASE_URL}/invoice-schedules/messages/${encodeURIComponent(messageId)}`,
    {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to get schedule creation status: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }

  return response.json();
}

/**
 * Create a schedule and resolve once the orchestrator has finished (RS schedule
 * + DV mirror + line items). Polls {@link getInvoiceScheduleCreationStatus}
 * until a terminal status. Resolves with the final status (whose `rsScheduleId`
 * lets the caller deep-link into RepairShopr); throws on Failed / Dead Letter /
 * timeout. Mirrors the job-creation accept-then-poll flow.
 */
export async function createInvoiceScheduleAndWait(
  payload: InvoiceScheduleCreateRequest,
  signal?: AbortSignal,
): Promise<InvoiceScheduleStatusResponse> {
  const { messageId } = await createInvoiceSchedule(payload, signal);
  const deadline = Date.now() + SCHEDULE_CREATE_MAX_POLL_MS;

  while (Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop -- sequential polling by design
    const status = await getInvoiceScheduleCreationStatus(messageId, signal);
    if (status.status === 'Succeeded') return status;
    if (status.status === 'Failed' || status.status === 'Dead Letter') {
      throw new Error(
        status.errors.length > 0
          ? status.errors.join('; ')
          : `Schedule creation ${status.status.toLowerCase()}`,
      );
    }
    // eslint-disable-next-line no-await-in-loop -- pace the poll loop
    await new Promise((resolve) => setTimeout(resolve, SCHEDULE_CREATE_POLL_INTERVAL_MS));
  }

  throw new Error('Schedule creation timed out. Check the Alerts page for status.');
}

export async function updateInvoiceSchedule(
  id: string,
  payload: Omit<InvoiceScheduleUpdateRequest, 'scheduleId'>,
  signal?: AbortSignal
): Promise<InvoiceScheduleDetail> {
  const response = await fetch(`${API_BASE_URL}/invoice-schedules/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to update invoice schedule: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }

  return response.json();
}

/**
 * Wait for the async line add/remove operations returned by an update to finish.
 * Line *edits* apply synchronously; adds/removes are orchestrator messages, so we
 * poll each {@link getInvoiceScheduleCreationStatus} to a terminal status (reusing
 * the same machinery as schedule create). Resolves with the ops that failed or did
 * not finish in time — never rejects, so a partial failure can be surfaced without
 * losing the successful edits. Note: RS reflects the change immediately, but the
 * Dataverse mirror (and so this UI) catches up on the next sm4rt_sync_rs2dv run.
 */
export async function waitForScheduleLineOps(
  ops: PendingScheduleLineOp[],
  signal?: AbortSignal,
): Promise<{ failed: PendingScheduleLineOp[] }> {
  const deadline = Date.now() + SCHEDULE_CREATE_MAX_POLL_MS;
  const pending = [...ops];
  const failed: PendingScheduleLineOp[] = [];

  while (pending.length > 0 && Date.now() < deadline) {
    for (let i = pending.length - 1; i >= 0; i -= 1) {
      // eslint-disable-next-line no-await-in-loop -- sequential polling by design
      const status = await getInvoiceScheduleCreationStatus(pending[i].messageId, signal);
      if (status.status === 'Succeeded') {
        pending.splice(i, 1);
      } else if (status.status === 'Failed' || status.status === 'Dead Letter') {
        failed.push(pending[i]);
        pending.splice(i, 1);
      }
    }
    if (pending.length > 0) {
      // eslint-disable-next-line no-await-in-loop -- pace the poll loop
      await new Promise((resolve) => setTimeout(resolve, SCHEDULE_CREATE_POLL_INTERVAL_MS));
    }
  }

  // Anything still pending at the deadline is reported as not-yet-applied.
  return { failed: [...failed, ...pending] };
}

export async function incrementInvoiceScheduleLine(
  scheduleId: string,
  payload: IncrementScheduleLineRequest,
  signal?: AbortSignal,
): Promise<IncrementScheduleLineResult> {
  const response = await fetch(
    `${API_BASE_URL}/invoice-schedules/${encodeURIComponent(scheduleId)}/lines/increment`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to increment schedule line: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }

  return response.json();
}

/** Request to add a NEW recurring line to an existing schedule (via orchestrator). */
export interface AddScheduleLineRequest {
  name: string;
  quantity: number;
  priceRetail: number;
  priceCost?: number;
  /** RepairShopr product id (links the line to the commercial product). */
  productId?: number;
  description?: string;
  taxable?: boolean;
  /** RS recurrence type; defaults server-side to 1 (recurring). */
  recurringTypeId?: number;
  /** Recurring lines pass false; defaults server-side to false. */
  oneTimeCharge?: boolean;
  /**
   * Add a second line for a SKU the schedule already bills. The server refuses
   * a duplicate-SKU add by default (silent double-charge guard); set this only
   * when a second line for the same SKU is intended (e.g. a second tenant).
   */
  allowDuplicateSku?: boolean;
}

/**
 * Add a new recurring line to an existing schedule. Routed through the
 * orchestrator (the only path that can add a line to a RepairShopr schedule),
 * so the API returns 202 Accepted — the line appears after the orchestrator runs.
 */
export async function addScheduleLine(
  scheduleId: string,
  payload: AddScheduleLineRequest,
  signal?: AbortSignal,
): Promise<{ status: string; scheduleId: string }> {
  const response = await fetch(
    `${API_BASE_URL}/invoice-schedules/${encodeURIComponent(scheduleId)}/lines/add`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to add schedule line: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }
  return response.json();
}

export async function deleteInvoiceSchedule(id: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/invoice-schedules/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to delete invoice schedule: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }
}
